import { useState } from "react";
import RequestModal from "./components/RequestModal.js";


const steps = [
    {
      title: "Find the Right Creators",
      text: "Paste your App Store link. FlueTrack finds creators whose audiences match your app and growth goals."
    },
    {
      title: "Track Every Install",
      text: "Know exactly which creators drive installs, subscriptions, and revenue. No guesswork."
    },
    {
      title: "Automated Payouts",
      text: "Reward creators automatically based on performance. We handle tracking, calculations, and payouts."
    }
  ];
  
  const faqs = [
    {
      q: "How secure is FlueTrack?",
      a: "We use encrypted connections, secure payment providers, and industry-standard security practices. As the platform grows, we'll continue investing in security and compliance."
    },
    {
      q: "How do I get access?",
      a: "Reserve your spot for $1. Once we reach 50 reservations, we'll open FlueTrack to early adopters. You'll receive 14 days of free access to test the platform. If it's not a fit, we'll refund your reservation fee."
    },
    {
      q: "What if the platform never launches?",
      a: "If we don't launch, every reservation is refunded. No forms, no support tickets, no hassle."
    },
    {
      q: "Can I pay with a credit card, not crypto?",
      a: "Yes. We accept Visa and Mastercard. A crypto wallet is optional."
    },
    {
      q: "Is this for my app?",
      a: "FlueTrack is designed for mobile apps that want to acquire users through creators and influencers. Whether you're an indie developer or a growing startup, you can use FlueTrack to launch creator-driven campaigns."
    },
    {
      q: "I'm a solo developer with a small budget. Is this for me?",
      a: "Yes. That's exactly who we built this for. Most influencer platforms require $2,000-$5,000 minimum spend and 6-month contracts. We don't. You can start with as little as $50, or even $0 + revenue share. No minimums. No hidden fees. Cancel anytime. We believe indie developers deserve enterprise-grade tools at indie prices."
    },
    {
      q: "How is this different from other platforms?",
      a: "Most influencer platforms focus on views, likes, and engagement. Fluetrack focuses on installs, subscriptions, and revenue. You can see exactly which creator generated results and reward them based on performance, not vanity metrics."
    }
  ];
  
  export default function App() {
    const [isDemoOpen, setIsDemoOpen] = useState(false);
    const [isEarlyAccessOpen, setIsEarlyAccessOpen] = useState(false);

    return (
      <div className="page">
        <header className="hero section">
          <nav className="nav">
            <img src="/logo.svg" alt="Fluetrack" className="brand-logo" />
            
            <div className="nav-links">
              <a href="#early-access">Early Access</a>
              <a href="#how-it-works">How it works</a>
              <a href="#faq">FAQ</a>
            </div>
          </nav>
  
          <div className="hero-content">
            <h1>
            Turn Creator Campaigns Into Measurable App Installs

            </h1>
            <p className="eyebrow">
            Find the right creators, track every install, and automate payouts from a single platform built for mobile apps.
            </p>

            <div className="hero-actions">
            <button className="btn btn-primary" onClick={() => {
                window.gtag?.("event", "request_demo_click", {
                  event_category: "engagement",
                  event_label: "demo_button",
                });
                setIsDemoOpen(true);
              }}>
              Request a demo
            </button>
            </div>
          </div>
        </header>
  
        <main>
          <section id="how-it-works" className="section">
            <p className="section-badge">HOW IT WORKS</p>
            <h2>3 simple steps to effortless efficiency</h2>
            <div className="grid three">
              {steps.map((step, index) => (
                <article className="card" key={step.title}>
                  <span className="step-pill">{index + 1}</span>
                  <h3>{step.title}</h3>
                  <p>{step.text}</p>
                </article>
              ))}
            </div>
          </section>
  
          <section id="early-access" className="section early-access-section">
            <div className="pricing">
            <p className="section-badge pricing-badge">EARLY ACCESS</p>

              <h2 className="pricing-title">Early Access Offer</h2>

              <p className="pricing-price">$1</p>
              <p className="pricing-subtitle">to reserve your spot</p>

              <p className="pricing-label">What&apos;s included:</p>
              <ul className="pricing-list">
                <li>14-day full access after launch</li>
                <li>Personal onboarding support</li>
                <li>30% lifetime discount for first 50 customers</li>
              </ul>

              <button className="btn btn-primary pricing-cta" onClick={() => {
                window.gtag?.("event", "request_early_access_click", {
                  event_category: "engagement",
                  event_label: "early_acceess_button",
                });
                setIsEarlyAccessOpen(true);
              }}>
                Book Early Access for 1$
              </button>
            </div>
          </section>
          <section id="faq" className="section">
            <p className="section-badge">FREQUENTLY ASKED QUESTIONS</p>
            <h2>Common questions answered</h2>
            <div className="faq-list">
              {faqs.map((item) => (
                <details className="faq-item" key={item.q}>
                  <summary>{item.q}</summary>
                  <p>{item.a}</p>
                </details>
              ))}
            </div>
          </section>
        </main>
  
        <footer className="section footer">
          <div className="footer-row">
            <p className="footer-left">FlueTrack © 2026. All rights reserved.</p>

            <a className="footer-email" href="mailto:support@fluetrack.com" aria-label="Email support">
              <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
                <path
                  d="M3 6.75A1.75 1.75 0 0 1 4.75 5h14.5A1.75 1.75 0 0 1 21 6.75v10.5A1.75 1.75 0 0 1 19.25 19H4.75A1.75 1.75 0 0 1 3 17.25V6.75Zm1.8-.25L12 11.35 19.2 6.5H4.8Zm14.7 1.55-6.94 4.67a1 1 0 0 1-1.12 0L4.5 8.05v9.2c0 .14.11.25.25.25h14.5a.25.25 0 0 0 .25-.25v-9.2Z"
                  fill="currentColor"
                />
              </svg>
              <span>support@fluetrack.com</span>
            </a>
          </div>
        </footer>
        <RequestModal 
          title='Request a demo'
          requestType='demo' 
          isOpen={isDemoOpen} 
          onClose={() => setIsDemoOpen(false)} 
        />
        <RequestModal 
          title='Book early access'
          requestType='early_access' 
          isOpen={isEarlyAccessOpen} 
          onClose={() => setIsEarlyAccessOpen(false)} 
        />
      </div>
    );
  }